import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import {
  fetchInterviewRequest,
  selectSessionData,
  selectLoading,
  selectError,
} from "@store/slices/interviewSlice";

import Header from "@components/Layout/Header";
import InterviewStart from "@/components/Interview/InterviewStart";
import ErrorAlert from "@components/UI/ErrorAlert";

/**
 * InterviewSetup Page
 * Format selection, consent acceptance, and media permission request
 */
const InterviewSetup = () => {
  const { sessionToken } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sessionData = useSelector(selectSessionData);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);

  // Fetch interview data on mount (needed when landing on setup page directly)
  useEffect(() => {
    if (sessionToken) {
      dispatch(fetchInterviewRequest(sessionToken));
    }
  }, [dispatch, sessionToken]);

  const handleStartInterview = (startData) => {
    // Persist startData so the Ready page survives a refresh
    sessionStorage.setItem(
      `interview_start_data_${sessionToken}`,
      JSON.stringify(startData)
    );
    navigate(`/interview/${sessionToken}/ready`);
  };

  const handleBack = () => {
    navigate(`/interview/${sessionToken}`, { replace: true });
  };

  // Check if there was actually an intro to go back to
  const hasIntroContent =
    sessionData &&
    sessionData.intro_type &&
    ((sessionData.intro_type === 'video' && sessionData.intro_video_url) ||
     (sessionData.intro_type === 'text' && sessionData.intro_text));

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button - Only show if intro was actually displayed */}
        {hasIntroContent && (
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center space-x-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
            >you
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Introduction</span>
            </button>
          </div>
        )}

        {/* Show InterviewStart component for format selection and consent */}
        <InterviewStart
          sessionData={sessionData}
          onStart={handleStartInterview}
          loading={loading}
        />

        {/* Error Alert */}
        {error && (
          <div className="mt-6">
            <ErrorAlert message={error} />
          </div>
        )}
      </main>
    </div>
  );
};

export default InterviewSetup;
